import DOMPurify from 'dompurify';

/** Soft caps for profiles.email_signature* (characters, UTF-16 length). */
export const EMAIL_SIGNATURE_MAX_HTML_CHARS = 32_000;
export const EMAIL_SIGNATURE_MAX_PLAIN_CHARS = 4_000;

/** Soft max display width for signature images in email clients. */
export const EMAIL_SIGNATURE_MAX_IMG_WIDTH = 560;

/** Declared width within this fraction of natural width ⇒ treat as full-res / Retina export. */
const RETINA_SIZE_TOLERANCE = 0.1;

const EMAIL_SIGNATURE_PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'br',
    'div',
    'em',
    'font',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'li',
    'ol',
    'p',
    'span',
    'strong',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
  ],
  ALLOWED_ATTR: [
    'align',
    'alt',
    'bgcolor',
    'border',
    'cellpadding',
    'cellspacing',
    'class',
    'color',
    'colspan',
    'height',
    'href',
    'rowspan',
    'src',
    'style',
    'target',
    'title',
    'valign',
    'width',
  ],
  ALLOW_DATA_ATTR: false,
  // http(s) / mailto only — blocks data: base64 blobs that inflate preferences
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
};

export type EmailSignatureSizeError = 'html_too_large' | 'plain_too_large';

/** Returns which field exceeds the size limit, or null if OK. */
export function getEmailSignatureSizeError(params: {
  html?: string | null;
  plain?: string | null;
}): EmailSignatureSizeError | null {
  const html = params.html || '';
  const plain = params.plain || '';
  if (html.length > EMAIL_SIGNATURE_MAX_HTML_CHARS) return 'html_too_large';
  if (plain.length > EMAIL_SIGNATURE_MAX_PLAIN_CHARS) return 'plain_too_large';
  return null;
}

/** Sanitize pasted / stored email signature HTML (browser only). */
export function sanitizeEmailSignatureHtml(html: string): string {
  if (!html || typeof window === 'undefined') return '';
  return DOMPurify.sanitize(html, EMAIL_SIGNATURE_PURIFY_CONFIG).trim();
}

/** Strip tags for plain-text fallback (browser). */
export function htmlToPlainText(html: string): string {
  if (!html || typeof window === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent || '';
  return text.replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function parsePositivePx(value: string | null | undefined): number | null {
  if (!value) return null;
  const n = Number.parseInt(String(value).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readStylePx(style: string, prop: 'width' | 'height'): number | null {
  const re = new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*(\\d+)px`, 'i');
  const match = style.match(re);
  return match ? parsePositivePx(match[1]) : null;
}

function getDeclaredWidth(img: HTMLImageElement): number | null {
  return (
    parsePositivePx(img.getAttribute('width')) ||
    parsePositivePx(img.style.width?.endsWith('px') ? img.style.width : null) ||
    readStylePx(img.getAttribute('style') || '', 'width')
  );
}

function getDeclaredHeight(img: HTMLImageElement): number | null {
  return (
    parsePositivePx(img.getAttribute('height')) ||
    parsePositivePx(img.style.height?.endsWith('px') ? img.style.height : null) ||
    readStylePx(img.getAttribute('style') || '', 'height')
  );
}

function applyImgDisplaySize(img: HTMLImageElement, width: number, height: number | null): void {
  const w = Math.max(1, Math.round(width));
  img.setAttribute('width', String(w));
  // Attributes help Outlook; style.height=auto lets max-width:100% shrink without stretching.
  if (height && height > 0) {
    img.setAttribute('height', String(Math.max(1, Math.round(height))));
  } else {
    img.removeAttribute('height');
  }
  img.style.width = `${w}px`;
  img.style.maxWidth = '100%';
  img.style.height = 'auto';
}

function loadImageNaturalSize(src: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const probe = new Image();
    const timer = window.setTimeout(() => {
      probe.onload = null;
      probe.onerror = null;
      resolve(null);
    }, 4000);
    probe.onload = () => {
      window.clearTimeout(timer);
      if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
      } else {
        resolve(null);
      }
    };
    probe.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    probe.src = src;
  });
}

function isLikelyFullResolutionDim(declared: number | null, natural: number): boolean {
  if (!declared) return true;
  return Math.abs(declared - natural) / natural <= RETINA_SIZE_TOLERANCE;
}

/**
 * Normalize signature images for email (MySignature / Retina exports often ship 2× assets).
 * Width may be halved when it matches the file’s natural size; height is always derived
 * from the natural aspect ratio so a stale 2× height cannot stretch the preview.
 */
export async function normalizeEmailSignatureHtml(html: string): Promise<string> {
  if (!html || typeof window === 'undefined') return html || '';
  const clean = sanitizeEmailSignatureHtml(html);
  if (!clean) return '';

  const doc = new DOMParser().parseFromString(clean, 'text/html');
  const images = Array.from(doc.body.querySelectorAll('img'));
  if (images.length === 0) return clean;

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src) return;

      const declaredW = getDeclaredWidth(img);
      const declaredH = getDeclaredHeight(img);
      const natural = await loadImageNaturalSize(src);

      let targetW: number;
      let targetH: number | null;

      if (natural) {
        if (!declaredW || isLikelyFullResolutionDim(declaredW, natural.width)) {
          targetW = Math.max(1, Math.round(natural.width / 2));
        } else {
          targetW = declaredW;
        }
        // Never trust declared height (often left at full pixel size while width was fixed).
        targetH = Math.max(1, Math.round(natural.height * (targetW / natural.width)));
      } else if (declaredW) {
        const looksRetinaDeclared =
          declaredW >= 360 || (declaredH != null && declaredH >= 360);
        targetW = looksRetinaDeclared ? Math.max(1, Math.round(declaredW / 2)) : declaredW;
        // Without natural size, drop height so CSS height:auto keeps aspect in preview.
        targetH =
          declaredH && looksRetinaDeclared
            ? Math.max(1, Math.round(declaredH / 2))
            : null;
      } else if (declaredH) {
        const h = declaredH >= 360 ? Math.max(1, Math.round(declaredH / 2)) : declaredH;
        img.removeAttribute('width');
        img.setAttribute('height', String(h));
        img.style.width = 'auto';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        return;
      } else {
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        return;
      }

      if (targetW > EMAIL_SIGNATURE_MAX_IMG_WIDTH) {
        const scale = EMAIL_SIGNATURE_MAX_IMG_WIDTH / targetW;
        targetW = EMAIL_SIGNATURE_MAX_IMG_WIDTH;
        if (targetH) targetH = Math.round(targetH * scale);
      }

      applyImgDisplaySize(img, targetW, targetH);
    })
  );

  return doc.body.innerHTML.trim();
}

/** Sanitize + normalize images — use on paste, load, preview, and send. */
export async function prepareEmailSignatureHtml(
  html: string | null | undefined
): Promise<string> {
  const raw = (html || '').trim();
  if (!raw) return '';
  return normalizeEmailSignatureHtml(raw);
}
